'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import IntegrationCard from './IntegrationCard';

const STORAGE_KEY = 'ecosystem-listing';

const PARTNER_TYPES = new Set(['technology', 'partner']);

const FILTER_OPTIONS = [
    {
        value: '',
        title: 'Select a partner based on your needs',
        description: 'Browse all integrations and partners',
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-5">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
                />
            </svg>
        )
    },
    {
        value: 'cmp:partner',
        title: 'Solution Partners',
        description:
            'Looking for support with your migration or implementation? Find the partner to meet your needs and ensure a smooth transition.',
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-5">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                />
            </svg>
        )
    },
    {
        value: 'cmp:technology',
        title: 'Technology Partners',
        description:
            'Extend the power of dotCMS by connecting the tools your team already relies on through flexible, no-code integrations.',
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-5">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z"
                />
            </svg>
        )
    },
    {
        value: 'int:Built-In',
        title: 'Integrations, Built-In',
        description:
            "Hit the ground running with native connections to the industry's leading platforms, ready to use right out of the box.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-5">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
                />
            </svg>
        )
    },
    {
        value: 'int:Plugin',
        title: 'Integrations, Plugin',
        description:
            'Expand your dotCMS setup with installable plugins that add powerful new capabilities tailored to your specific stack.',
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-5">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
                />
            </svg>
        )
    }
];

// Filter options restricted to integrations only (for /integrations route)
const INTEGRATION_FILTER_OPTIONS = [
    { ...FILTER_OPTIONS[0], title: 'All Integrations', description: 'Browse all integrations' },
    FILTER_OPTIONS[3],
    FILTER_OPTIONS[4]
];

function CategoryDropdown({ options, values, onChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const [alignRight, setAlignRight] = useState(false);
    const ref = useRef(null);

    const allOption = options[0];
    let buttonIcon, buttonLabel;
    if (values.length === 0) {
        buttonIcon = allOption.icon;
        buttonLabel = allOption.title;
    } else if (values.length === 1) {
        const opt = options.find((o) => o.value === values[0]);
        buttonIcon = opt?.icon || allOption.icon;
        buttonLabel = opt?.title || values[0];
    } else {
        buttonIcon = allOption.icon;
        buttonLabel = `${values.length} selected`;
    }

    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    function handleToggle() {
        if (!isOpen && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const panelWidth = 384; // w-96
            setAlignRight(rect.left + panelWidth > window.innerWidth - 16);
        }
        setIsOpen((o) => !o);
    }

    function handleOptionClick(optionValue) {
        if (optionValue === '') {
            onChange([]);
            setIsOpen(false);
        } else {
            const already = values.includes(optionValue);
            onChange(already ? values.filter((v) => v !== optionValue) : [...values, optionValue]);
        }
    }

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={handleToggle}
                className="flex h-full items-center gap-3 rounded-xl border border-fuchsia-400 bg-fuchsia-50 px-4 py-2 text-sm text-fuchsia-700 transition-colors hover:bg-fuchsia-100 focus:outline-none focus:ring-2 focus:ring-fuchsia-500">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-fuchsia-100 text-fuchsia-600">
                    {buttonIcon}
                </span>
                <span className="font-semibold">{buttonLabel}</span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`size-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            <div
                className={`absolute top-full z-20 mt-2 w-96 origin-top overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl transition-all duration-200 ${
                    alignRight ? 'right-0' : 'left-0'
                } ${
                    isOpen
                        ? 'pointer-events-auto scale-y-100 opacity-100'
                        : 'pointer-events-none scale-y-95 opacity-0'
                }`}>
                <div className="py-2">
                    {options.map((option) => {
                        const isSelected =
                            option.value === ''
                                ? values.length === 0
                                : values.includes(option.value);
                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleOptionClick(option.value)}
                                className={`flex w-full items-start gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50 ${
                                    isSelected ? 'bg-indigo-50' : ''
                                }`}>
                                <span
                                    className={`mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl ${
                                        isSelected
                                            ? 'bg-fuchsia-100 text-fuchsia-600'
                                            : 'bg-gray-100 text-gray-600'
                                    }`}>
                                    {option.icon}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span
                                        className={`block text-sm font-semibold ${
                                            isSelected ? 'text-indigo-700' : 'text-gray-900'
                                        }`}>
                                        {option.title}
                                    </span>
                                    <span className="mt-1 block text-xs leading-snug text-gray-500">
                                        {option.description}
                                    </span>
                                </span>
                                {option.value !== '' && (
                                    <span
                                        className={`ml-auto mt-1 flex size-5 shrink-0 items-center justify-center rounded ${
                                            isSelected
                                                ? 'bg-fuchsia-600 text-white'
                                                : 'border border-gray-300 bg-white'
                                        }`}>
                                        {isSelected && (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                className="size-3">
                                                <path
                                                    fillRule="evenodd"
                                                    d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        )}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// filter value format: '' = all, 'int:{badge}' = integration badge, 'cmp:{type1}' = company type
// mode: 'ecosystem' shows all content; 'integrations' shows only integrations with integration filters
export function EcosystemListing({ integrations, companies, mode = 'ecosystem' }) {
    const storageKey = mode === 'integrations' ? 'integrations-listing' : STORAGE_KEY;
    const availableFilters = mode === 'integrations' ? INTEGRATION_FILTER_OPTIONS : FILTER_OPTIONS;

    companies = companies.filter((c) => PARTNER_TYPES.has(c.type1));

    const stored =
        typeof sessionStorage !== 'undefined'
            ? JSON.parse(sessionStorage.getItem(storageKey) || '{}')
            : {};

    // Migrate from old single-value filter format to multi-select array
    const initialFilters = stored.filters || (stored.filter ? [stored.filter] : []);

    const [search, setSearch] = useState(stored.search || '');
    const [filters, setFilters] = useState(initialFilters);

    useEffect(() => {
        sessionStorage.setItem(storageKey, JSON.stringify({ search, filters }));
    }, [search, filters, storageKey]);

    const hasActiveFilter = !!(search || filters.length > 0);

    const filtered = useMemo(() => {
        let results = [];

        if (filters.length === 0) {
            if (mode === 'integrations') {
                results = integrations.map((i) => ({ ...i, _contentType: 'Integration' }));
            } else {
                results = [
                    ...integrations.map((i) => ({ ...i, _contentType: 'Integration' })),
                    ...companies.map((c) => ({ ...c, _contentType: 'Company' }))
                ];
            }
        } else {
            const normalize = (s) => s?.toLowerCase().replace(/[-_\s]/g, '');
            for (const f of filters) {
                if (f.startsWith('int:')) {
                    const badge = normalize(f.slice(4));
                    results.push(
                        ...integrations
                            .filter((i) => {
                                const b =
                                    typeof i.badge === 'string' ? i.badge : i.badge?.selectValue;
                                return normalize(b) === badge;
                            })
                            .map((i) => ({ ...i, _contentType: 'Integration' }))
                    );
                } else if (f.startsWith('cmp:')) {
                    const type1 = f.slice(4);
                    results.push(
                        ...companies
                            .filter((c) => c.type1 === type1)
                            .map((c) => ({ ...c, _contentType: 'Company' }))
                    );
                }
            }
            // Deduplicate by identifier
            const seen = new Set();
            results = results.filter((r) => {
                if (seen.has(r.identifier)) return false;
                seen.add(r.identifier);
                return true;
            });
        }

        if (search) {
            const lower = search.toLowerCase();
            results = results.filter((r) => r.title?.toLowerCase().includes(lower));
        }

        return results.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }, [integrations, companies, filters, search, mode]);

    function handleClear() {
        setSearch('');
        setFilters([]);
    }

    return (
        <div>
            <div className="mb-8 flex flex-wrap items-stretch gap-3">
                <CategoryDropdown
                    options={availableFilters}
                    values={filters}
                    onChange={setFilters}
                />
                <div className="relative flex flex-1 items-center">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search integrations and partners..."
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-3 flex items-center justify-center text-gray-400 hover:text-gray-600"
                            aria-label="Clear search">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="size-4">
                                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                            </svg>
                        </button>
                    )}
                </div>
                {hasActiveFilter && (
                    <button
                        onClick={handleClear}
                        className="rounded-xl border border-gray-300 px-4 text-sm text-gray-600 transition-colors hover:bg-gray-100">
                        Clear
                    </button>
                )}
            </div>

            {filtered.length === 0 ? (
                <div className="py-16 text-center text-gray-500">
                    <p className="text-lg mb-4">No results found.</p>
                    <button
                        onClick={handleClear}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100">
                        Clear filters
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {filtered.map((item) => (
                        <IntegrationCard
                            key={item.identifier}
                            integration={item}
                            contentType={item._contentType}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
